import React, { useState } from "react";
import { Eye, EyeOff } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import styles from "../../../styles/__login.module.scss";
import Footer from "../../../components/footer/Footer";
import { useDispatch } from "react-redux";
import {postResetPassword} from "../../../reducer/resetPassword/resetPasswordReducer"
import { useLocation } from "react-router-dom"; // ✅ Get reset token from URL params
import CountryCodeSelect from "../../../components/country-code-select/CountryCodeSelect";
import {
  DEFAULT_COUNTRY_ISO,
  findCountryByIso,
} from "../../../constants/countries";
import {
  getMobileLengthIssue,
  limitMobileDigits,
  mobileLengthErrorText,
} from "../../../utils/phoneValidation";

const ForgotPassword = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const [showPasswordNew, setShowPasswordNew] = useState(false);
  const [showPasswordCon, setShowPasswordCon] = useState(false);
  const [showPasswordCurrent, setShowPasswordCurrent] = useState(false);
  const token = queryParams.get("token");
  const [password, setPassword] = useState("");
  const [username, setUsername] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [errors, setErrors] = useState({});
  console.log('token:', token);
  const [countryIso, setCountryIso] = useState(DEFAULT_COUNTRY_ISO);
  const [mobileNumber, setMobileNumber] = useState("");
  // Same country list (with search and flags) as every other mobile number field in the apps.
  const selectedCountryData = findCountryByIso(countryIso);
  // Too many digits for the selected country (libphonenumber-js metadata) is flagged at once.
  const mobileLengthIssue = getMobileLengthIssue(mobileNumber, countryIso);
  const mobileError =
    errors?.mobileNumber ||
    (mobileLengthIssue?.tooLong
      ? mobileLengthErrorText(t, i18n.language, selectedCountryData, mobileLengthIssue)
      : "");

  // ✅ Password rules
  const passwordRules = {
    length: { test: (pwd) => pwd.length >= 8, message: "At least 8 characters" },
    lowercase: { test: (pwd) => /[a-z]/.test(pwd), message: "At least one lowercase letter" },
    uppercase: { test: (pwd) => /[A-Z]/.test(pwd), message: "At least one uppercase letter" },
    number: { test: (pwd) => /[0-9]/.test(pwd), message: "At least one number" },
    special: {
      test: (pwd) => /[!@#$%^&*(),.?\":{}|<>]/.test(pwd),
      message: "At least one special character",
    },
  };

  // ✅ Validation function
  const validateForm = () => {
    const newErrors = {};

    // New password validation
    if (!password.trim()) {
      newErrors.password = "New password is required";
    } else {
      const failedRule = Object.values(passwordRules).find((rule) => !rule.test(password));
      if (failedRule) newErrors.password = failedRule.message;
    }

    // Confirm password validation
    if (!confirmPassword.trim()) {
      newErrors.confirmPassword = "Confirm password is required";
    } else if (password !== confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // ✅ Handle Submit
  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    const payload = {
      new_password:password,
      confirm_password:confirmPassword,
      reset_token:token, // ✅ Now included from params
    };
    dispatch(postResetPassword(payload));

    console.log("Reset Password Data:", payload);

    // API call can go here using Redux or fetch
    // navigate("/"); // Redirect after success
  };

  return (
    <div className={styles["main-container"]}>
      <main className={styles["main-content"]}>
        <div className={styles["content-wrapper"]}>
          {/* Illustration */}
          <div className={styles["illustration"]}>
            <div className={styles["doctor-illustration"]}>
              <img
                src="/ForgotPassword.png"
                alt="Doctor illustration"
                className={styles["doctor-image"]}
              />
            </div>
          </div>

          {/* Reset Password Form */}
          <div className={styles["login-form-container"]}>
            <h2>{t("forgotPassword") || "Forgot Password"}</h2>

            <form onSubmit={handleSubmit} className={styles["login-form"]}>
              {/* Username */}
              {/* <div className={styles["form-group"]}>
                <label htmlFor="username">Username</label>
                <input
                  type="text"
                  id="username"
                  placeholder="Enter your username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className={styles["form-input"]}
                />
                {errors?.username && (
                  <p className={styles["rule-invalid"]}>{errors?.username || "error invalid"}</p>
                )}
              </div> */}

              {/* Mobile Number */}
              <div className={styles["form-group"]}>
                <label htmlFor="mobile">Mobile Number</label>
                <div className={styles["mobile-input-container"]}>
                  <CountryCodeSelect
                    className={styles["country-selector"]}
                    value={countryIso}
                    onChange={(country) => setCountryIso(country.iso)}
                  />
                  <input
                    type="tel"
                    id="mobile"
                    placeholder="770 123 4567"
                    inputMode="numeric"
                    value={mobileNumber}
                    onChange={(e) =>
                      // Digits only, no leading 0, and no more digits than the
                      // selected country's mobile numbers have.
                      setMobileNumber(limitMobileDigits(e.target.value, countryIso))
                    }
                    aria-invalid={!!mobileError || undefined}
                    className={styles["phone-input"]}
                  />
                </div>
                {mobileError && (
                  <p role="alert" className={styles["rule-invalid"]}>
                    {mobileError}
                  </p>
                )}
              </div>


              {/* New Password */}
              <div className={styles["form-group"]}>
                <label htmlFor="password">{t("newPassword")}</label>
                <div className={styles["password-wrapper"]}>
                  <input
                    type={showPasswordNew ? "text" : "password"}
                    id="password"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className={styles["form-input"]}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPasswordNew(!showPasswordNew)}
                    className={styles["password-toggle"]}
                  >
                    {!showPasswordNew ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>

                {/* Password rule indicators */}
                {/* <div className={styles["password-rules"]}>
                  {Object.keys(passwordRules).map((ruleKey) => {
                    const rule = passwordRules[ruleKey];
                    const isValid = rule.test(password);
                    return (
                      <p
                        key={ruleKey}
                        className={isValid ? styles["rule-valid"] : styles["rule-invalid"]}
                      >
                        {isValid ? "✔" : "✖"} {rule.message}
                      </p>
                    );
                  })}
                </div> */}

                {errors.password && (
                  <p className={styles["rule-invalid"]}>{errors.password}</p>
                )}
              </div>

              {/* Confirm Password */}
              <div className={styles["form-group"]}>
                <label htmlFor="confirmPassword">{t("confirmPassword")}</label>
                <div className={styles["password-wrapper"]}>
                  <input
                    type={showPasswordCon ? "text" : "password"}
                    id="confirmPassword"
                    placeholder="••••••••"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className={styles["form-input"]}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPasswordCon(!showPasswordCon)}
                    className={styles["password-toggle"]}
                  >
                    {!showPasswordCon ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
                {errors.confirmPassword && (
                  <p className={styles["rule-invalid"]}>{errors.confirmPassword}</p>
                )}
              </div>

              {/* Submit Button */}
              <button type="submit" className={styles["login-btn"]}>
                {t("resetPassword")}
              </button>
            </form>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default ForgotPassword